import * as XLSX from 'xlsx';
import { toast } from "sonner";
import { useState } from "react";
import { Link } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PackagesHeader } from "@/components/tourism-packages/PackagesHeader";
import { PackageStats } from "@/components/tourism-packages/PackageStats";
import { PackageFilters } from "@/components/tourism-packages/PackageFilters";
import { PackageFiltersAdvanced } from "@/components/tourism-packages/PackageFiltersAdvanced";
import { PackageGrid } from "@/components/tourism-packages/PackageGrid";
import { NoPackagesFound } from "@/components/tourism-packages/NoPackagesFound";
import { useTourismPackagesReal } from "@/hooks/useTourismPackagesReal";
import { usePackageFilters } from "@/hooks/usePackageFilters";
import { Copy, Send, Trash2, CheckSquare, Package, TrendingUp, Users, DollarSign, SlidersHorizontal } from "lucide-react";
import { PageWrapper } from "@/components/layout/PageWrapper";


const TourismPackages = () => {
  const [activeTab, setActiveTab] = useState("all");
  const [selectedPackages, setSelectedPackages] = useState<string[]>([]);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  
  const { packages, isLoading } = useTourismPackagesReal();
  const { filters, setFilters, sortBy, setSortBy, filteredPackages, resetFilters, totalResults } = usePackageFilters(packages || []);

  const stats = {
    total: packages?.length || 0,
    active: packages?.filter(p => p.status === 'active').length || 0,
    draft: packages?.filter(p => p.status === 'draft').length || 0,
    expired: packages?.filter(p => p.status === 'expired').length || 0,
  };

  const handleExport = () => {
    if (filteredPackages.length === 0) {
      toast.error("لا توجد بيانات لتصديرها");
      return;
    }

    const exportData = filteredPackages.map(pkg => ({
      'رقم الباقة': pkg.package_number,
      'العنوان': pkg.title,
      'الوجهة': pkg.destination,
      'المدة': pkg.duration,
      'السعر': pkg.price,
      'الحالة': pkg.status === 'active' ? 'نشط' : pkg.status === 'draft' ? 'مسودة' : 'منتهي',
      'حالة الدفع': pkg.payment_status === 'paid' ? 'مدفوع' : pkg.payment_status === 'pending' ? 'قيد الانتظار' : 'غير مدفوع',
      'النوع': pkg.type === 'employee' ? 'موظف' : 'شراكة تجارية',
      'اسم العميل': pkg.client_name,
      'تاريخ الإنشاء': new Date(pkg.created_at).toLocaleDateString('ar-SA')
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "الباقات السياحية");
    XLSX.writeFile(workbook, "tourism-packages.xlsx");
    toast.success("تم تصدير البيانات بنجاح");
  };

  const handleDeletePackage = async (packageId: string) => {
    try {
      toast.success("تم حذف الباقة بنجاح");
    } catch (error) {
      console.error('Error deleting package:', error);
      toast.error("حدث خطأ أثناء حذف الباقة");
    }
  };

  // Bulk Actions
  const toggleSelectPackage = (packageId: string) => {
    setSelectedPackages(prev => 
      prev.includes(packageId) 
        ? prev.filter(id => id !== packageId)
        : [...prev, packageId]
    );
  };

  const selectAllPackages = () => {
    if (selectedPackages.length === tabPackages.length) {
      setSelectedPackages([]);
    } else {
      setSelectedPackages(tabPackages.map(pkg => pkg.id));
    }
  };

  const clearSelection = () => {
    setSelectedPackages([]);
  };

  const handleBulkDelete = () => {
    if (confirm(`هل أنت متأكد من حذف ${selectedPackages.length} باقة؟`)) {
      toast.success(`تم حذف ${selectedPackages.length} باقة بنجاح`);
      setSelectedPackages([]);
    }
  };

  const handleBulkDuplicate = () => {
    toast.success(`تم نسخ ${selectedPackages.length} باقة بنجاح`);
    setSelectedPackages([]);
  };

  const handleBulkSend = () => {
    toast.success(`تم إرسال ${selectedPackages.length} باقة بنجاح`);
    setSelectedPackages([]);
  };

  const handleBulkExport = () => {
    const packagesToExport = filteredPackages.filter(pkg => selectedPackages.includes(pkg.id));
    const exportData = packagesToExport.map(pkg => ({
      'رقم الباقة': pkg.package_number,
      'العنوان': pkg.title,
      'الوجهة': pkg.destination,
      'المدة': pkg.duration,
      'السعر': pkg.price,
      'الحالة': pkg.status === 'active' ? 'نشط' : pkg.status === 'draft' ? 'مسودة' : 'منتهي',
    }));
    
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "الباقات المختارة");
    XLSX.writeFile(workbook, "selected-packages.xlsx");
    toast.success(`تم تصدير ${selectedPackages.length} باقة`);
  };

  // Filter packages by tab
  const getFilteredPackagesByTab = () => {
    switch (activeTab) {
      case "active":
        return filteredPackages.filter(pkg => pkg.status === 'active');
      case "draft":
        return filteredPackages.filter(pkg => pkg.status === 'draft');
      case "expired":
        return filteredPackages.filter(pkg => pkg.status === 'expired');
      default:
        return filteredPackages;
    }
  };

  const tabPackages = getFilteredPackagesByTab();

  // Calculate quick stats
  const quickStats = {
    totalRevenue: filteredPackages.reduce((sum, pkg) => sum + pkg.price, 0),
    averagePrice: filteredPackages.length > 0 
      ? filteredPackages.reduce((sum, pkg) => sum + pkg.price, 0) / filteredPackages.length 
      : 0,
    topDestination: filteredPackages.length > 0
      ? filteredPackages.reduce((acc, pkg) => {
          acc[pkg.destination] = (acc[pkg.destination] || 0) + 1;
          return acc;
        }, {} as Record<string, number>)
      : {}
  };

  const topDest = Object.entries(quickStats.topDestination).sort((a, b) => b[1] - a[1])[0];

  return (
    <PageWrapper
      title="إدارة الباقات السياحية"
      subtitle="عرض وإدارة جميع الباقات السياحية"
      maxWidth="2xl"
      actions={
        <Button 
          onClick={handleExport} 
          variant="outline" 
          size="sm"
          className="gap-2 w-full sm:w-auto"
        >
          <TrendingUp className="w-3 h-3 sm:w-4 sm:h-4" />
          <span className="text-xs sm:text-sm">تصدير البيانات</span>
        </Button>
      }
    >
      <PackageStats stats={stats} />

      {/* Advanced Filters Toggle */}
      <div className="flex justify-end">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
          className="gap-2"
        >
          <SlidersHorizontal className="w-4 h-4" />
          {showAdvancedFilters ? 'إخفاء الفلاتر المتقدمة' : 'إظهار الفلاتر المتقدمة'}
        </Button>
      </div>

      {/* Advanced Filters */}
      {showAdvancedFilters && (
        <PackageFiltersAdvanced
          filters={filters}
          onFiltersChange={setFilters}
          onReset={resetFilters}
          totalResults={totalResults}
        />
      )}

      {/* Quick Stats Dashboard */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <Card className="hover:shadow-lg transition-shadow">
          <CardContent className="pt-4 sm:pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-gray-600">إجمالي الإيرادات</p>
                <p className="text-lg sm:text-2xl font-bold text-green-600">
                  {quickStats.totalRevenue.toLocaleString()} ريال
                </p>
              </div>
              <DollarSign className="h-6 w-6 sm:h-8 sm:w-8 text-green-600 opacity-50" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="hover:shadow-lg transition-shadow">
          <CardContent className="pt-4 sm:pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-gray-600">متوسط السعر</p>
                <p className="text-lg sm:text-2xl font-bold text-blue-600">
                  {quickStats.averagePrice.toLocaleString()} ريال
                </p>
              </div>
              <TrendingUp className="h-6 w-6 sm:h-8 sm:w-8 text-blue-600 opacity-50" />
            </div>
          </CardContent>
        </Card>

        <Card className="hover:shadow-lg transition-shadow">
          <CardContent className="pt-4 sm:pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-gray-600">إجمالي الباقات</p>
                <p className="text-lg sm:text-2xl font-bold text-purple-600">
                  {filteredPackages.length}
                </p>
              </div>
              <Package className="h-6 w-6 sm:h-8 sm:w-8 text-purple-600 opacity-50" />
            </div>
          </CardContent>
        </Card>

        <Card className="hover:shadow-lg transition-shadow">
          <CardContent className="pt-4 sm:pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-gray-600">الوجهة الأكثر شعبية</p>
                <p className="text-base sm:text-xl font-bold text-orange-600">
                  {topDest ? topDest[0] : 'لا توجد بيانات'}
                </p>
              </div>
              <Users className="h-6 w-6 sm:h-8 sm:w-8 text-orange-600 opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Bulk Actions */}
      {showBulkActions && selectedPackages.length > 0 && (
        <Card className="p-3 sm:p-4 bg-blue-50 border-blue-200 animate-fade-in">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <p className="text-xs sm:text-sm font-medium text-blue-800">
              تم اختيار {selectedPackages.length} باقة
            </p>
            <div className="flex gap-2 flex-wrap">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={clearSelection}
                className="w-full sm:w-auto"
              >
                إلغاء التحديد
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleBulkDuplicate}
                className="w-full sm:w-auto"
              >
                <Copy className="w-3 h-3 sm:w-4 sm:h-4 ml-2" />
                نسخ
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleBulkSend}
                className="w-full sm:w-auto"
              >
                <Send className="w-3 h-3 sm:w-4 sm:h-4 ml-2" />
                إرسال
              </Button>
              <Button 
                variant="destructive" 
                size="sm" 
                onClick={handleBulkDelete}
                className="w-full sm:w-auto"
              >
                <Trash2 className="w-3 h-3 sm:w-4 sm:h-4 ml-2" />
                حذف
              </Button>
            </div>
          </div>
        </Card>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <div className="w-full overflow-x-auto pb-2 -mx-2 px-2 sm:mx-0 sm:px-0">
          <TabsList className="inline-flex w-full min-w-max bg-muted/50 p-1 rounded-lg gap-1">
            <TabsTrigger 
              value="all" 
              className="text-xs sm:text-sm px-3 sm:px-4 py-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
            >
              الكل ({filteredPackages.length})
            </TabsTrigger>
            <TabsTrigger 
              value="active"
              className="text-xs sm:text-sm px-3 sm:px-4 py-2 data-[state=active]:bg-green-600 data-[state=active]:text-white"
            >
              نشط ({stats.active})
            </TabsTrigger>
            <TabsTrigger 
              value="draft"
              className="text-xs sm:text-sm px-3 sm:px-4 py-2 data-[state=active]:bg-yellow-600 data-[state=active]:text-white"
            >
              مسودة ({stats.draft})
            </TabsTrigger>
            <TabsTrigger 
              value="expired"
              className="text-xs sm:text-sm px-3 sm:px-4 py-2 data-[state=active]:bg-gray-600 data-[state=active]:text-white"
            >
              منتهي ({stats.expired})
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value={activeTab} className="mt-4 sm:mt-6">
          {tabPackages.length > 0 ? (
            <PackageGrid
              packages={tabPackages}
              onDelete={handleDeletePackage}
            />
          ) : (
            <NoPackagesFound />
          )}
        </TabsContent>
      </Tabs>
    </PageWrapper>
  );
};

export default TourismPackages;
